import * as React from 'react';
import useAuth from '@sdk/authentication/useAuth';
import gettext from '@sdk/lang/gettext';
import { Feature } from '@sdk/common/features';
import { Sections } from '@sdk/components/ui/anchorMenu/createSections';

import getPremiumTooltipId from '../../../premiumGuard/getPremiumTooltipId';
import PremiumGuard from '../../../premiumGuard/PremiumGuard';
import usePremiumGuard from '../../../premiumGuard/usePremiumGuard';
import CollapsibleSettingsSection from '../CollapsibleSettingsSection';
import SettingsHeader from '../SettingsHeader';
import SettingsSection from '../SettingsSection';
import SettingsSwitcherRow from '../SettingsSwitcherRow';
import { PremiumModalNames } from '../../../premiumGuard/constants/modalNames';
import { useUserOrgSettings } from '../../UserOrgSettingsContext';

type Props = {
  sectionsConfig: Sections;
};

const Unavailability: React.FC<React.PropsWithChildren<Props>> = ({ sectionsConfig }) => {
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { usersSetAvailability, unavailabilitySetApprovalEnabled } = userOrgSettings;

  return (
    <SettingsSection pBottom={20}>
      <CollapsibleSettingsSection
        withSeparator
        isActive={usersSetAvailability}
        headerItem={
          <PremiumGuard
            premiumGuardModalName={PremiumModalNames.unavailability}
            hasAccess={isOrgFeatureEnabled(Feature.unavailability)}
            tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.unavailability)]}>
            <>
              <SettingsHeader
                ref={sectionsConfig.refs.unavailability}
                title={gettext('Unavailability')}
                premiumGuard={getPremiumGuardConfig(Feature.unavailability)}
              />
              <SettingsSwitcherRow
                disabled={!currentUser.isAdmin}
                subtitle={gettext('Allow employees to set and edit their unavailability.')}
                value={usersSetAvailability}
                onChange={(value): void => onUserOrgSettingsChange({ usersSetAvailability: value })}
              />
            </>
          </PremiumGuard>
        }
        contentItem={
          <PremiumGuard
            premiumGuardModalName={PremiumModalNames.unavailabilityApproval}
            hasAccess={isOrgFeatureEnabled(Feature.unavailabilityApproval)}
            tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.unavailabilityApproval)]}>
            <SettingsSwitcherRow
              pBottom={20}
              disabled={!currentUser.isAdmin}
              title={gettext('Approval')}
              subtitle={gettext('When enabled, unavailability changes require manager’s approval.')}
              value={unavailabilitySetApprovalEnabled}
              onChange={(value): void =>
                onUserOrgSettingsChange({ unavailabilitySetApprovalEnabled: value })
              }
              premiumGuard={getPremiumGuardConfig(Feature.unavailabilityApproval)}
            />
          </PremiumGuard>
        }
      />
    </SettingsSection>
  );
};

export default Unavailability;
